import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { getFundInvite } from 'services/messagingTopics'
import { faPlain } from 'utilities/helper/format'

type TShareMethod = 'copy_link' | 'copy_text' | 'share'

/**
 * «دعوت اعضا به بله و ایتا» — the manager's own invite link for this fund.
 *
 * Every member who opens Hamyan in Bale or Eitaa gets the fund's news free and
 * in full instead of by SMS. The link is this manager's (one per manager per
 * fund), so what it brought is theirs to see: opened, and actually joined.
 */
export default function FundInvitePage() {
    const { moneyPool } = useParams()
    const { data, isLoading, isError } = useQuery(
        ['fund-invite', moneyPool],
        () => getFundInvite(moneyPool as string),
        { enabled: !!moneyPool }
    )
    const [copied, setCopied] = useState<TShareMethod | null>(null)

    const report = (method: TShareMethod) =>
        posthog.capture('miniapp_invite_link_shared', {
            fund_id: Number(moneyPool),
            method,
        })

    const copy = (value: string, method: TShareMethod) => {
        if (!navigator.clipboard) {
            toast.info('متن را دستی کپی کنید')
            return
        }
        navigator.clipboard
            .writeText(value)
            .then(() => {
                setCopied(method)
                report(method)
                window.setTimeout(() => setCopied(null), 2000)
            })
            .catch(() => toast.info('متن را دستی کپی کنید'))
    }

    const share = () => {
        if (!data) return
        if (!navigator.share) {
            copy(data.text, 'copy_text')
            return
        }
        navigator
            .share({ text: data.text })
            .then(() => report('share'))
            .catch(() => undefined)
    }

    if (isLoading) return <DotsLoading />
    if (isError || !data)
        return (
            <div className="min-h-screen bg-white">
                <Header title="دعوت اعضا به بله و ایتا" />
                <p className="p-6 text-center text-sm text-text-secondary">
                    فقط مدیران صندوق لینک دعوت دارند.
                </p>
            </div>
        )

    const { reach, stats } = data
    const share100 = reach.total
        ? Math.round((reach.messenger / reach.total) * 100)
        : 0

    return (
        <div className="min-h-screen bg-[#FAFAFA] pb-10">
            <div className="bg-white">
                <Header title="دعوت اعضا به بله و ایتا" />
            </div>

            <div className="space-y-3 px-4 pt-4">
                <section className="rounded-card border border-divider bg-white p-4">
                    <p className="text-sm font-bold text-black">
                        {faPlain.format(reach.messenger)} از{' '}
                        {faPlain.format(reach.total)} عضو خبرها را در بله یا
                        ایتا می‌گیرند
                    </p>
                    <div
                        className="mt-3 h-2 overflow-hidden rounded-full bg-sunken"
                        role="progressbar"
                        aria-valuenow={share100}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <div
                            className="h-full rounded-full bg-brand"
                            style={{ width: `${share100}%` }}
                        />
                    </div>
                    {reach.off > 0 && (
                        <p className="mt-3 text-xs leading-6 text-text-body">
                            {faPlain.format(reach.off)} نفر هنوز نه؛ آن‌ها خبرها
                            را فقط داخل همیان یا با پیامک می‌بینند. لینک زیر را
                            در گروه صندوق بفرستید: در بله و ایتا خبرها رایگان،
                            کامل‌تر و همان لحظه می‌رسد.
                        </p>
                    )}
                </section>

                <section className="rounded-card border border-divider bg-white p-4">
                    <p className="mb-2 text-xs text-text-secondary">
                        لینک دعوت شما
                    </p>
                    <p
                        dir="ltr"
                        className="break-all rounded-lg bg-sunken p-3 text-left text-sm font-bold text-black"
                        data-testid="invite-url"
                    >
                        {data.urls.web}
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={() => copy(data.urls.web, 'copy_link')}
                            className="h-10 rounded-lg border border-brand text-sm font-bold text-brand"
                        >
                            {copied === 'copy_link' ? 'کپی شد ✓' : 'کپی لینک'}
                        </button>
                        <button
                            type="button"
                            onClick={share}
                            className="h-10 rounded-lg bg-brand text-sm font-bold text-white"
                        >
                            اشتراک‌گذاری
                        </button>
                    </div>
                </section>

                <section className="rounded-card border border-divider bg-white p-4">
                    <p className="mb-2 text-xs text-text-secondary">
                        متنی که در گروه صندوق می‌فرستید
                    </p>
                    <p className="whitespace-pre-wrap text-sm leading-7 text-black">
                        {data.text}
                    </p>
                    <button
                        type="button"
                        onClick={() => copy(data.text, 'copy_text')}
                        className="mt-3 h-10 w-full rounded-lg border border-brand text-sm font-bold text-brand"
                    >
                        {copied === 'copy_text' ? 'کپی شد ✓' : 'کپی متن دعوت'}
                    </button>
                </section>

                <section className="rounded-card border border-divider bg-white p-4">
                    <p className="text-sm font-bold text-black">از لینک شما</p>
                    <p className="mt-1 text-xs leading-6 text-text-body">
                        {faPlain.format(stats.opened)} نفر باز کردند،{' '}
                        {faPlain.format(stats.joined)} نفر وارد شدند.
                    </p>
                    <p className="mt-2 text-xs leading-6 text-text-secondary">
                        «وارد شدند» یعنی همین لینک حساب بله یا ایتای آن‌ها را به
                        همیان وصل کرد.
                    </p>
                </section>
            </div>
        </div>
    )
}
